import { LeaveEntry, LeaveType } from '@techgitter/contracts';

const API_BASE_URL = '/api';

export interface CreateLeaveDto {
  userId: string;
  userFullName: string;
  startAt: string; // ISO 8601 datetime
  endAt: string; // ISO 8601 datetime
  leaveType: LeaveType;
  notes?: string;
}

export interface UpdateLeaveDto {
  startAt?: string;
  endAt?: string;
  leaveType?: LeaveType;
  notes?: string;
}

/**
 * Fetch leave entries within a date range for the calendar view.
 */
export async function getLeaveEntries(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<LeaveEntry[]> {
  const params = new URLSearchParams({ from, to });
  const response = await fetch(`${API_BASE_URL}/leave?${params}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch leave entries: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new leave entry.
 */
export async function createLeaveEntry(data: CreateLeaveDto): Promise<LeaveEntry> {
  const response = await fetch(`${API_BASE_URL}/leave`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create leave entry: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update an existing leave entry.
 */
export async function updateLeaveEntry(
  uuid: string,
  data: UpdateLeaveDto,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/leave/${uuid}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to update leave entry: ${response.status} ${response.statusText}`);
  }
}

/**
 * Delete a leave entry.
 */
export async function deleteLeaveEntry(uuid: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/leave/${uuid}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete leave entry: ${response.status} ${response.statusText}`);
  }
}
